<script lang="ts">
    import { onDestroy, onMount } from 'svelte';
    import { api } from '../../../framework/js/api.js';
    import { toast } from '../../../framework/js/toast.js';
    import { spinner } from '../../../framework/js/spinner.js';
    import {
        AppEmptyState,
        AppLoadingState,
        AppPageHeader,
        AppToolbar,
        StatusBadge,
    } from '../../../framework/components/app';
    import KnowledgeSourceModal from './KnowledgeSourceModal.svelte';
    import {
        formatKnowledgeDate,
        formatKnowledgeStatus,
        isKnowledgeIndexing,
        knowledgeFailedHint,
        knowledgeSourceMeta,
        knowledgeStatusTone,
        knowledgeTileIcon,
        type KnowledgeScope,
        type KnowledgeSourceItem,
    } from './knowledgeAdmin';
    import '../admin-list.css';

    const POLL_INTERVAL_MS = 2500;
    const MAX_POLL_MS = 5 * 60 * 1000;

    export let scope: KnowledgeScope = 'platform';
    export let briefTypeUuid: string | null = null;
    export let scopeLabel = '';
    export let pageTitle = 'Platform knowledge';
    export let pageSubtitle =
        'Curated Xplorit methodology and quality rules injected into AI requests.';

    let items: KnowledgeSourceItem[] = [];
    let filtered: KnowledgeSourceItem[] = [];
    let search = '';
    let isLoading = false;
    let modalOpen = false;
    let saving = false;
    let editing: KnowledgeSourceItem | null = null;
    let retryingUuid: string | null = null;
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let pollStartedAt = 0;
    let isPolling = false;

    $: pendingCount = items.filter((i) => i.status === 'draft' || i.status === 'processing').length;
    $: filtered = filterItems(items, search);

    onMount(() => {
        void loadItems().then(() => {
            if (hasPendingItems(items)) startPolling();
        });
    });

    onDestroy(() => {
        stopPolling();
    });

    function filterItems(list: KnowledgeSourceItem[], phrase: string): KnowledgeSourceItem[] {
        const q = phrase.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (item) =>
                item.title.toLowerCase().includes(q) ||
                item.content_kind.toLowerCase().includes(q) ||
                (item.file_name?.toLowerCase().includes(q) ?? false),
        );
    }

    function hasPendingItems(list: KnowledgeSourceItem[]): boolean {
        return list.some((i) => i.status === 'draft' || i.status === 'processing');
    }

    function startPolling(): void {
        if (pollTimer) return;
        isPolling = true;
        pollStartedAt = Date.now();
        pollTimer = setInterval(() => {
            void pollOnce();
        }, POLL_INTERVAL_MS);
    }

    function stopPolling(): void {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
        isPolling = false;
    }

    async function pollOnce(): Promise<void> {
        if (Date.now() - pollStartedAt > MAX_POLL_MS) {
            stopPolling();
            return;
        }
        await refreshItems();
        if (!hasPendingItems(items)) {
            stopPolling();
        }
    }

    async function refreshItems(): Promise<void> {
        try {
            const response: any =
                scope === 'platform'
                    ? await api.getAdminPlatformKnowledge()
                    : await api.getAdminBriefTypeKnowledge(briefTypeUuid!);

            if (response?.success) {
                items = response.data?.items ?? [];
            }
        } catch (error) {
            console.error(error);
        }
    }

    $: if (briefTypeUuid) {
        void loadItems();
    }

    async function loadItems(): Promise<void> {
        isLoading = true;
        spinner.show();
        try {
            const response: any =
                scope === 'platform'
                    ? await api.getAdminPlatformKnowledge()
                    : await api.getAdminBriefTypeKnowledge(briefTypeUuid!);

            if (response?.success) {
                items = response.data?.items ?? [];
            } else {
                items = [];
                toast.showNotification(false, response?.message || 'Failed to load knowledge sources');
            }
        } catch (error) {
            console.error(error);
            items = [];
            toast.showNotification(false, 'Error loading knowledge sources');
        } finally {
            spinner.hide();
            isLoading = false;
            if (hasPendingItems(items)) {
                startPolling();
            }
        }
    }

    function openCreate(): void {
        editing = null;
        modalOpen = true;
    }

    function openEdit(item: KnowledgeSourceItem): void {
        editing = item;
        modalOpen = true;
    }

    function closeModal(): void {
        modalOpen = false;
        editing = null;
    }

    function openTile(item: KnowledgeSourceItem): void {
        if (item.file_url) {
            window.open(item.file_url, '_blank', 'noopener,noreferrer');
            return;
        }
        openEdit(item);
    }

    async function handleSave(
        event: CustomEvent<{
            title: string;
            body_text: string;
            content_kind: string;
            pin_in_prompt: boolean;
            retrieval_priority: number;
            agent_allowlist: string[];
            file: File | null;
            allow_override: boolean;
        }>,
    ): Promise<void> {
        saving = true;
        try {
            const payload = event.detail;
            const formData = new FormData();
            formData.append('title', payload.title);
            if (payload.body_text) formData.append('body_text', payload.body_text);
            formData.append('content_kind', payload.content_kind);
            formData.append('pin_in_prompt', payload.pin_in_prompt ? '1' : '0');
            formData.append('retrieval_priority', String(payload.retrieval_priority));
            formData.append('publish', '1');
            if (payload.allow_override) formData.append('allow_override', '1');
            if (payload.agent_allowlist.length) {
                payload.agent_allowlist.forEach((agent) => formData.append('agent_allowlist[]', agent));
            }
            if (payload.file) formData.append('file', payload.file);

            let response: any;
            if (editing) {
                const updateData: Record<string, unknown> = {
                    title: payload.title,
                    body_text: payload.body_text,
                    content_kind: payload.content_kind,
                    pin_in_prompt: payload.pin_in_prompt,
                    retrieval_priority: payload.retrieval_priority,
                    agent_allowlist: payload.agent_allowlist,
                    allow_override: payload.allow_override,
                    reindex: true,
                };
                response = await api.updateAdminKnowledge(editing.uuid, updateData);
            } else if (scope === 'platform') {
                response = await api.createAdminPlatformKnowledge(formData);
            } else {
                response = await api.createAdminBriefTypeKnowledge(briefTypeUuid!, formData);
            }

            if (response?.success) {
                toast.showNotification(true, editing ? 'Knowledge source updated' : 'Knowledge source created');
                closeModal();
                await loadItems();
                startPolling();
            } else {
                toast.showNotification(false, response?.message || 'Save failed');
            }
        } catch (error) {
            console.error(error);
            toast.showNotification(false, 'Save failed');
        } finally {
            saving = false;
        }
    }

    async function publishItem(item: KnowledgeSourceItem): Promise<void> {
        const response: any = await api.publishAdminKnowledge(item.uuid);
        toast.showNotification(!!response?.success, response?.message || (response?.success ? 'Queued for indexing' : 'Publish failed'));
        if (response?.success) {
            await loadItems();
            startPolling();
        }
    }

    async function retryItem(item: KnowledgeSourceItem): Promise<void> {
        retryingUuid = item.uuid;
        try {
            const response: any = await api.publishAdminKnowledge(item.uuid);
            toast.showNotification(!!response?.success, response?.message || (response?.success ? 'Retrying…' : 'Retry failed'));
            if (response?.success) {
                await loadItems();
                startPolling();
            }
        } catch (error) {
            console.error(error);
            toast.showNotification(false, 'Retry failed');
        } finally {
            retryingUuid = null;
        }
    }

    async function archiveItem(item: KnowledgeSourceItem): Promise<void> {
        const response: any = await api.archiveAdminKnowledge(item.uuid);
        toast.showNotification(!!response?.success, response?.message || (response?.success ? 'Archived' : 'Archive failed'));
        if (response?.success) await loadItems();
    }

    async function deleteItem(item: KnowledgeSourceItem): Promise<void> {
        const response: any = await api.deleteAdminKnowledge(item.uuid);
        toast.showNotification(!!response?.success, response?.message || (response?.success ? 'Deleted' : 'Delete failed'));
        if (response?.success) await loadItems();
    }
</script>

<div class="page-shell admin-list knowledge-admin">
    <AppPageHeader title={pageTitle} subtitle={pageSubtitle} showAction={false}>
        <svelte:fragment slot="actions">
            <button type="button" class="action-btn" on:click={openCreate}>
                <i class="plus circle icon" aria-hidden="true"></i>
                <span>Add source</span>
            </button>
        </svelte:fragment>
    </AppPageHeader>

    {#if isLoading}
        <AppLoadingState label="Loading knowledge sources…" />
    {:else if items.length === 0}
        <AppEmptyState
            icon="book"
            title="No knowledge sources yet"
            description="Add methodology text, rubrics, or reference files for the AI to use."
        >
            <button type="button" class="empty-action-btn" on:click={openCreate}>Add source</button>
        </AppEmptyState>
    {:else}
        {#if isPolling && pendingCount > 0}
            <p class="polling-banner" role="status" aria-live="polite">
                <i class="circle notch loading icon" aria-hidden="true"></i>
                Indexing {pendingCount} {pendingCount === 1 ? 'source' : 'sources'}…
            </p>
        {/if}

        <AppToolbar
            bind:searchValue={search}
            searchPlaceholder="Search by title or kind…"
            resultsCount={filtered.length}
        />

        <ul class="tile-grid">
            {#each filtered as item (item.uuid)}
                <li class="tile" class:tile--indexing={isKnowledgeIndexing(item.status)}>
                    <div class="tile-header">
                        <span class="tile-icon" class:tile-icon--indexing={isKnowledgeIndexing(item.status)} aria-hidden="true">
                            {#if isKnowledgeIndexing(item.status)}
                                <i class="circle notch loading icon"></i>
                            {:else}
                                <i class="{knowledgeTileIcon(item)} icon"></i>
                            {/if}
                        </span>
                        <div class="tile-actions">
                            <button
                                type="button"
                                class="icon-action"
                                aria-label="Edit {item.title}"
                                on:click={() => openEdit(item)}
                            >
                                <i class="edit icon" aria-hidden="true"></i>
                            </button>
                            {#if item.status !== 'archived'}
                                <button
                                    type="button"
                                    class="icon-action"
                                    aria-label="Archive {item.title}"
                                    on:click={() => archiveItem(item)}
                                >
                                    <i class="archive icon" aria-hidden="true"></i>
                                </button>
                            {/if}
                            <button
                                type="button"
                                class="icon-action danger"
                                aria-label="Delete {item.title}"
                                on:click={() => deleteItem(item)}
                            >
                                <i class="trash icon" aria-hidden="true"></i>
                            </button>
                        </div>
                    </div>

                    <button type="button" class="tile-body" on:click={() => openTile(item)}>
                        <h3 class="tile-title">{item.title}</h3>
                        <p class="tile-meta">{knowledgeSourceMeta(item)}</p>
                        <p class="tile-date">{formatKnowledgeDate(item.updated_at)}</p>
                    </button>

                    <div class="tile-footer">
                        <div class="tile-status">
                            <StatusBadge
                                label={formatKnowledgeStatus(item.status)}
                                tone={knowledgeStatusTone(item.status)}
                                loading={isKnowledgeIndexing(item.status)}
                            />
                            {#if item.status === 'failed' && knowledgeFailedHint(item)}
                                <p class="tile-fail-hint">{knowledgeFailedHint(item)}</p>
                            {/if}
                        </div>
                        <div class="tile-footer-actions">
                            {#if item.status === 'failed'}
                                <button
                                    type="button"
                                    class="footer-link"
                                    on:click={() => retryItem(item)}
                                    disabled={retryingUuid === item.uuid}
                                >
                                    {retryingUuid === item.uuid ? 'Retrying…' : 'Retry'}
                                </button>
                            {:else if item.status !== 'published' && item.status !== 'archived'}
                                <button type="button" class="footer-link" on:click={() => publishItem(item)}>
                                    Publish
                                </button>
                            {/if}
                        </div>
                    </div>
                </li>
            {/each}
        </ul>

        {#if filtered.length === 0}
            <p class="no-results">No sources match your search.</p>
        {/if}
    {/if}
</div>

<KnowledgeSourceModal
    open={modalOpen}
    {saving}
    {editing}
    scope={scope === 'brief_type' ? 'brief_type' : 'platform'}
    {scopeLabel}
    on:close={closeModal}
    on:save={handleSave}
/>

<style>
    .knowledge-admin .action-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-5);
        background: var(--primary);
        color: var(--white);
        border: 0;
        border-radius: var(--radius-cta);
        font-weight: 500;
        cursor: pointer;
        box-shadow: var(--shadow-cta);
    }

    .knowledge-admin .action-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .empty-action-btn {
        margin-top: var(--space-4);
        border: 0;
        background: var(--primary);
        color: var(--white);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--border-radius-button);
        font-weight: 500;
        cursor: pointer;
    }

    .empty-action-btn:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .polling-banner {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        margin: 0 0 var(--space-4);
        padding: var(--space-3) var(--space-4);
        background: var(--status-info-soft);
        border-radius: var(--radius-button);
        color: var(--status-info-ink);
        font-size: var(--text-sm);
    }

    .tile-grid {
        list-style: none;
        margin: var(--space-4) 0 0;
        padding: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
        gap: var(--space-4);
    }

    .tile {
        display: flex;
        flex-direction: column;
        background: var(--white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        min-height: 11rem;
    }

    .tile-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        padding: var(--space-4) var(--space-4) 0;
    }

    .tile-icon {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.75rem;
        height: 2.75rem;
        border-radius: var(--radius-button);
        background: var(--status-info-soft);
        color: var(--primary);
    }

    .tile--indexing {
        border-color: var(--primary);
    }

    .tile-icon--indexing {
        background: var(--status-info-soft);
        color: var(--status-info-ink);
    }

    .tile-actions {
        display: flex;
        gap: var(--space-1);
    }

    .tile-body {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
        padding: var(--space-3) var(--space-4) var(--space-4);
        border: 0;
        background: transparent;
        text-align: left;
        cursor: pointer;
        font: inherit;
        color: var(--grey);
        min-width: 0;
    }

    .tile-body:hover {
        background: var(--greyish);
    }

    .tile-body:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .tile-title {
        margin: 0;
        font-size: var(--text-base);
        font-weight: 500;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        line-height: 1.4;
    }

    .tile-meta,
    .tile-date {
        margin: 0;
        font-size: var(--text-sm);
        color: var(--grey-secondary);
    }

    .tile-footer {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--space-2);
        padding: var(--space-3) var(--space-4) var(--space-4);
        border-top: 1px solid var(--border-color);
    }

    .tile-status {
        display: flex;
        flex-direction: column;
        gap: var(--space-1);
        min-width: 0;
    }

    .tile-fail-hint {
        margin: 0;
        font-size: var(--text-xs);
        color: var(--grey-secondary);
        line-height: 1.4;
    }

    .tile-footer-actions {
        display: flex;
        flex-shrink: 0;
        gap: var(--space-2);
    }

    .footer-link {
        border: 0;
        background: transparent;
        color: var(--primary);
        font-size: var(--text-sm);
        font-weight: 500;
        text-decoration: underline;
        cursor: pointer;
        padding: 0;
    }

    .footer-link:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .footer-link:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .icon-action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 2.25rem;
        height: 2.25rem;
        border: 0;
        border-radius: var(--radius-button);
        background: transparent;
        color: var(--grey-secondary);
        cursor: pointer;
    }

    .icon-action:hover {
        background: var(--greyish);
        color: var(--grey);
    }

    .icon-action.danger:hover {
        color: var(--status-danger-ink);
    }

    .icon-action:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
    }

    .no-results {
        margin: var(--space-6) 0;
        text-align: center;
        color: var(--grey-secondary);
    }

    @media (max-width: 768px) {
        .tile-grid {
            grid-template-columns: 1fr;
        }
    }
</style>
